import React, { useEffect, useState } from "react";
import { Form, Button, SelectPicker } from "rsuite";
import { toast } from "react-toastify";
import "./QrGeneration.css"
import { generateQr, getSkuMaster } from "../services/apiService";
import GenerateImg from "../assets/generate_bg.png";

class SKUMaster {
  skuId: number;
  skuCode: string = "";
  constructor(data: any) {
    this.skuId = data?.skuId || null;
    this.skuCode = data?.skuCode || "";
  }
}

const QrGeneration = () => {
  const [skuCode, setSkuCode] = useState<SKUMaster[]>([]);
  const [qrGenerateData, setQrGenerateData] = useState({
    skuCode: "",
    quantity: "",
    purchaseOrderNumber: "",
  });
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    productDetails();
  }, []);

  const productDetails = async () => {
    try {
      const response = await getSkuMaster();
      
      // Check if response has the expected structure
      if (response?.data) {
        let skuData = [];
        
        // Handle different possible response structures
        if (response?.data?.responseCode === 200) {
          // If data is nested under response.data.data
          skuData = response?.data?.data || response?.data;
        } else {
          // If data is directly in response.data
          skuData = Array.isArray(response?.data) ? response?.data : [];
        }
        
        // Convert to SKUMaster instances if needed
        const formattedData = skuData.map((item: any) => new SKUMaster(item));
        setSkuCode(formattedData);
        
        if (formattedData.length === 0) {
          toast.info("No SKU Master data available");
        }
      } else {
        console.error("Unexpected response structure:", response);
        toast.error("Failed to fetch SKU Master - invalid response");
      }
    } catch (error) {
      console.error("Error fetching SKU Master:", error);
      toast.error("Failed to fetch SKU Master");
    }
  };

  const validateForm = (): { valid: boolean; message: string } => {
    if (!qrGenerateData.skuCode) {
      return { valid: false, message: "Please select SKU Master" };
    }
    if (!qrGenerateData.quantity) {
      return { valid: false, message: "Please enter the quantity" };
    }
    if (!qrGenerateData.purchaseOrderNumber) {
      return { valid: false, message: "Please enter the purchase order number" };
    }
    
    // Additional validation for quantity (should be a positive number)
    const quantity = parseInt(qrGenerateData.quantity);
    if (isNaN(quantity) || quantity <= 0) {
      return { valid: false, message: "Please enter a valid quantity (positive number)" };
    }
    
    return { valid: true, message: "" };
  };

  const onSubmit = async (e: React.MouseEvent) => {
    e.preventDefault();

    const { valid, message } = validateForm();
    
    if (!valid) {
      toast.error(message);
      return;
    }

    setIsLoading(true);
    try {
      // Find the selected SKU details for better payload
      const selectedSku = skuCode.find(sku => sku.skuId.toString() === qrGenerateData.skuCode);
      
      let payload = {
        skuCode: selectedSku ? selectedSku.skuCode : qrGenerateData.skuCode,
        purchaseOrderNumber: qrGenerateData.purchaseOrderNumber.trim(),
        quantity: Number(qrGenerateData.quantity),
      };

      console.log("Payload being sent:", payload);

      const response = await generateQr(payload);
      console.log("Generate QR response:", response);
      
      if (response?.data?.code === 200) {
        toast.info(response?.data?.message || "QR code generation in progress!");
        resetForm();
        
        // If the response contains QR code data or download link, handle it here
        if (response?.data?.qrCodeUrl || response?.data?.downloadLink) {
          // You can add logic here to download or display the QR code
          console.log("QR Code URL:", response?.data?.qrCodeUrl || response?.data?.downloadLink);
        }
      } else {
        const errorMessage = response?.data?.message /* || response?.message */ || "QR generation failed";
        toast.error(errorMessage);
      }
    } catch (error: any) {
      console.error("QR Generation Error:", error);
      
      // Handle different types of errors
      if (error.response?.data?.message) {
        toast.error(error?.response?.data?.message);
      } else if (error?.message) {
        toast.error(error?.message);
      } else {
        toast.error("An error occurred during QR generation");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setQrGenerateData({
      skuCode: "",
      quantity: "",
      purchaseOrderNumber: "",
    });
  };

  const handleInput = (value: string, type: string) => {
    if (type === "quantity") {
      const numericValue = value.replace(/[^0-9]/g, '');
      setQrGenerateData((prev) => ({ ...prev, [type]: numericValue }));
    } else if (type === "purchaseOrderNumber") {
      const stringValue = value.replace(/[^a-zA-Z0-9\s-]/g, '');
      setQrGenerateData((prev) => ({ ...prev, [type]: stringValue }));
    } else {
      setQrGenerateData((prev) => ({ ...prev, [type]: value }));
    }
  };

  return (
    <div className="qr-generation-page">
      <div className="qr-generation-container">
        <div className="header">
          <img src={GenerateImg} alt="img" className="header-img" />
        </div>
        <div className="form-header">
          <h2 className="welcome-title">Generate QR codes</h2>
          <p className="welcome-subtitle">Select SKU Master and enter details...</p>
        </div>
       
        <Form fluid className="qr-generation-form">
          <div className="form-row">
            <Form.Group controlId="skuCode" className="form-group">
              <Form.ControlLabel className="form-label">
                SKU Master <span className="required">*</span>
              </Form.ControlLabel>
             
              <SelectPicker
                data={skuCode?.map((item: SKUMaster) => ({
                  label: item.skuCode,
                  value: item.skuId.toString()
                }))}
                labelKey="label"
                valueKey="value"
                style={{ width: '100%' }}
                placeholder="Choose SKU Master"
                onChange={(value: any) => {
                  handleInput(value || "", "skuCode");
                }}
                value={qrGenerateData.skuCode}
                className="form-select"
                loading={skuCode.length === 0}
                disabled={skuCode.length === 0}
              />
              {skuCode.length === 0 && (
                <small style={{ color: '#666', marginTop: '4px', display: 'block' }}>
                  Loading SKU Master data...
                </small>
              )}
            </Form.Group>
            
            <Form.Group controlId="quantity" className="form-group">
              <Form.ControlLabel className="form-label">
                Quantity <span className="required">*</span>
              </Form.ControlLabel>
              <Form.Control
                name="quantity"
                type="text"
                value={qrGenerateData.quantity}
                onChange={(value: string) => handleInput(value || "", "quantity")}
                placeholder="Enter the quantity"
                className="form-input"
              />
            </Form.Group>
            
            <Form.Group controlId="purchaseOrderNumber" className="form-group">
              <Form.ControlLabel className="form-label">
                Purchase Order Number <span className="required">*</span>
              </Form.ControlLabel>
              <Form.Control
                name="purchaseOrderNumber"
                type="text"
                value={qrGenerateData.purchaseOrderNumber}
                onChange={(value: string) => handleInput(value || "", "purchaseOrderNumber")}
                placeholder="Enter the purchase order number"
                className="form-input"
              />
            </Form.Group>
          </div>
          
          <Button
            appearance="primary"
            onClick={onSubmit}
            disabled={isLoading || skuCode.length === 0}
            className="generate-button"
          >
            {isLoading ? "Generating..." : "Generate QR Code"}
          </Button>
        </Form>
      </div>
    </div>
  );
};

export default QrGeneration;